import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

export const playlistFormSchema = z
    .object({
        name: z.string().min(1, { message: 'Name is required' }),
        header: z.union([z.literal(''), z.null(), z.string()]).optional(),
        partnerName: z.union([z.null(), z.string()]).optional(),
        partnerPlaylistAlias: z.union([z.null(), z.string()]).optional(),
        labels: z.array(z.string()).optional(),
        type: z.enum(['video', 'audio', 'playlists', 'playlist-of-playlists']),
        isPodcastPlaylist: z.union([z.string(), z.boolean()]).optional(),
        useLimitPerCategory: z.boolean().optional(),
        query: z.union([z.literal(''), z.null(), z.string()]).optional(),
        limit: z.number({ invalid_type_error: 'Must be number' }).min(1, 'Min value is 1').max(100, 'Max value is 100'),
        searchQuery: z.string(),
        metadata: z
            .array(
                z.object({
                    key: z.string().min(1, { message: 'Key is required' }),
                    value: z.string().min(1, { message: 'Value is required' }),
                })
            )
            .optional(),
        items: z
            .array(
                z.union([
                    z.object({
                        position: z.number().optional(),
                        isStatic: z.boolean().optional(),
                        asset: z.object({
                            id: z.number(),
                            provider: z.string(),
                            duration: z.number().optional(),
                            category: z.string(),
                        }),
                    }),
                    z.object({
                        position: z.number().optional(),
                        isStatic: z.boolean().optional(),
                        playlist: z.object({
                            id: z.string(),
                            provider: z.string(),
                            duration: z.number().optional(),
                        }),
                    }),
                ])
            )
            .optional(),
    })
    .superRefine((data, ctx) => {
        const hasPartnerName = Boolean(data.partnerName);
        const hasPartnerAlias = Boolean(data.partnerPlaylistAlias);

        if (hasPartnerName && !hasPartnerAlias) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Partner playlist alias is required when partner name is set',
                path: ['partnerPlaylistAlias'],
            });
        }

        if (hasPartnerAlias && !hasPartnerName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Partner name is required when partner playlist alias is set',
                path: ['partnerName'],
            });
        }
    });

export const resolver = zodResolver(playlistFormSchema);

// Empty string or a valid 3/6-digit hex color (e.g. #fff or #ffffff).
const hexColor = z
    .string()
    .regex(/^(#(?:[0-9a-f]{3}){1,2})?$/i, {
        message: 'Enter a valid hex color (e.g. #ffffff)',
    })
    .optional();

const imageUrl = z.string().optional();

export const themeFormSchema = z.object({
    themeTitle: z.string().min(1, { message: 'Theme title is required' }),
    hideIcon: z.boolean().optional(),
    background: z
        .object({
            lightModeColor: hexColor,
            darkModeColor: hexColor,
        })
        .optional(),
    videoCards: z
        .object({
            textColor: hexColor,
            highlightColor: hexColor,
        })
        .optional(),
    images: z
        .object({
            iconLight: imageUrl,
            iconDark: imageUrl,
            backgroundImageLight: imageUrl,
            backgroundImageDark: imageUrl,
        })
        .optional(),
});

export const themeResolver = zodResolver(themeFormSchema);
